import React, { useState, useEffect } from "react";
import {
  doc,
  setDoc,
  getDoc,
  collection,
  getDocs,
  query,
  where,
} from "firebase/firestore";
import { db } from "../firebase";
import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css";

// ✅ Firebase Storage
import { getStorage, ref, uploadBytes, getDownloadURL } from "firebase/storage";

const storage = getStorage();

function FamilyForm({ memberId, onSave, user }) {
  const [form, setForm] = useState({
    name: "",
    dob: "",
    bio: "",
    photoUrl: "",
    parentId: "",
    spouseId: "",
    childrenIds: [],
  });

  const [allMembers, setAllMembers] = useState([]);
  const [uploading, setUploading] = useState(false);

  // ✅ Fetch only this user's members for dropdowns
  useEffect(() => {
    const fetchMembers = async () => {
      if (!user) return;
      const q = query(
        collection(db, "FamilyTree"),
        where("ownerId", "==", user.uid)
      );
      const snapshot = await getDocs(q);
      const members = snapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
      }));
      setAllMembers(members);
    };
    fetchMembers();
  }, [user]);

  // ✅ Fetch existing member for edit
  useEffect(() => {
    if (memberId) {
      const fetchData = async () => {
        const docRef = doc(db, "FamilyTree", memberId);
        const docSnap = await getDoc(docRef);
        if (docSnap.exists()) {
          setForm({
            ...docSnap.data(),
            childrenIds: docSnap.data().childrenIds || [],
          });
        }
      };
      fetchData();
    }
  }, [memberId]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleChildrenChange = (e) => {
    const options = Array.from(e.target.selectedOptions).map((o) => o.value);
    setForm((prev) => ({ ...prev, childrenIds: options }));
  };

  // ✅ Handle Image Upload
  const handleFileChange = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    // ⛔ Limit size to 2 MB
    if (file.size > 1 * 1024 * 1024) {
      alert("File size exceeds 1MB. Please choose a smaller image.");
      return;
    }

    try {
      setUploading(true);
      const storageRef = ref(storage, `family_photos/${Date.now()}_${file.name}`);
      await uploadBytes(storageRef, file);
      const url = await getDownloadURL(storageRef);
      setForm((prev) => ({ ...prev, photoUrl: url }));
      alert("Image uploaded successfully!");
    } catch (err) {
      console.error("Image upload failed:", err);
      alert("Failed to upload image");
    } finally {
      setUploading(false);
    }
  };

  // ✅ Save family member with ownerId
  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const docRef = doc(db, "FamilyTree", memberId || Date.now().toString());
      await setDoc(
        docRef,
        {
          ...form,
          ownerId: user.uid, // ✅ attach logged-in user's UID
        },
        { merge: true }
      );
      alert("Family member saved successfully!");
      onSave();
    } catch (err) {
      console.error("Error saving family member:", err);
      alert("Error saving family member");
    }
  };

  return (
    <form
      onSubmit={handleSubmit}
      className="bg-white shadow-md p-6 rounded-lg space-y-4 w-full max-w-lg"
    >
      <h2 className="text-2xl font-bold mb-4 text-center">
        {memberId ? "Edit Family Member" : "Add Family Member"}
      </h2>

      {/* Name */}
      <div>
        <label className="block font-semibold mb-1">Full Name</label>
        <input
          type="text"
          name="name"
          value={form.name}
          onChange={handleChange}
          placeholder="Enter full name"
          className="w-full border px-3 py-2 rounded"
          required
        />
      </div>

      {/* DOB */}
      <div>
        <label className="block font-semibold mb-1">Date</label>
        <input
          type="text"
          name="dob"
          value={form.dob}
          onChange={handleChange}
          className="w-full border px-3 py-2 rounded"
        />
      </div>

      {/* Parent Dropdown */}
      <div className="mt-5">
        <label className="block font-semibold mb-1">Parent</label>
        <select
          name="parentId"
          value={form.parentId}
          onChange={handleChange}
          className="w-full border px-3 py-2 rounded"
        >
          <option value="">Select Parent</option>
          {allMembers
            .filter((m) => m.id !== memberId)
            .map((m) => (
              <option key={m.id} value={m.id}>
                {m.name}
              </option>
            ))}
        </select>
      </div>

      {/* Biography */}
      <div>
        <label className="block font-semibold mb-1">Biography</label>
        <ReactQuill
          theme="snow"
          value={form.bio}
          onChange={(value) => setForm((prev) => ({ ...prev, bio: value }))}
          modules={{
            toolbar: [
              [{ header: [1, 2, false] }],
              ["bold", "italic", "underline"],
              [{ list: "ordered" }, { list: "bullet" }],
              ["clean"],
            ],
          }}
          style={{ height: "200px" }}
        />
      </div>

      {/* Photo Upload */}
      <div>
        <label className="block font-semibold mb-1">Upload Photo</label>
        <input
          type="file"
          accept="image/*"
          onChange={handleFileChange}
          className="mb-2"
        />

        {/* Show uploaded photo */}
        {form.photoUrl && (
          <div className="mt-3">
            <img
              src={form.photoUrl}
              alt="Uploaded"
              className="w-32 h-32 object-cover rounded-full border"
            />
          </div>
        )}
      </div>

      {/* Buttons */}
      <div className="flex justify-end gap-3 mt-4">
        <button
          type="submit"
          disabled={uploading}
          className={`px-6 py-2 rounded text-white transition-colors ${
            uploading
              ? "bg-gray-400 cursor-not-allowed"
              : "bg-blue-500 hover:bg-blue-600"
          }`}
        >
          {uploading ? "Uploading..." : "Save"}
        </button>
        <button
          type="button"
          className="bg-gray-400 text-white px-6 py-2 rounded hover:bg-gray-500 transition-colors"
          onClick={onSave}
        >
          Cancel
        </button>
      </div>
    </form>
  );
}

export default FamilyForm;